import LOGO from "@/assets/images/Prud_Logo.png";
import { useForm, FormProvider } from "react-hook-form";
import useAuth from "../hooks/useAuth";
import { handleSuccessToast } from "@/Components/Uitoast/globalToastHandler";
import { ArrowLeft } from "lucide-react";
import UiButton from "@/UI-Components/Buttons/UiButton";
import { setApiServiceHeader } from "@/lib/Axios/axios.config";
import { useEffect } from "react";
import { inputUtility } from "@/utlities/input.utility";
import UiTextInput from "@/UI-Components/Input/UiTextInput";

const SignUpForm = ({ onToggle }: { onToggle: () => void }) => {
  const formMethods = useForm({
    mode: "onChange",
  });

  const { handleSubmit, getValues, setFocus } = formMethods;

  const {
    mutations: { registerUserMutation },
    states: {
      showOtpField,
      setShowOtpField,
      timer,
      setTimer,
      canResend,
      setCanResend,
    },
    routing: { navigate },
  } = useAuth();

  const handleResendOtp = () => {
    const mobile = getValues("mobile");
    const name = getValues("name");
    const email = getValues("email");

    const payload = {
      mobile,
      name,
      email,
    };

    if (mobile) {
      registerUserMutation.mutateAsync(payload).then(() => {
        setTimer(30);
        setCanResend(false);
        handleSuccessToast({ message: "OTP sent successfully" });
      });
    }
  };

  const onSendOtp = (data: any) => {
    const payload = {
      mobile: data.mobile,
      name: data.name,
      email: data.email,
    };

    registerUserMutation.mutateAsync(payload).then(() => {
      handleSuccessToast({ message: "OTP sent successfully" });
      setShowOtpField(true);
      setTimer(30);
      setCanResend(false);
    });
  };

  const onVerifyOtp = (data: any) => {
    const payload = {
      name: data.name,
      email: data.email,
      mobile: data.mobile,
      otp: data.otp,
    };

    registerUserMutation.mutateAsync(payload).then((response) => {
      handleSuccessToast({ message: "Sign Up successfully" });
      if (response?.data) {
        localStorage.setItem("token", response?.data?.token);
        setApiServiceHeader({
          key: "Authorization",
          value: response?.data?.token,
        });
        navigate("/agent/profile-details");
      }
    });
  };

  const handleBackToMobile = () => {
    setShowOtpField(false);
    setTimer(0);
    setCanResend(false);
    formMethods.setValue("otp", "");
  };

  const onSubmit = (data: any) => {
    if (!showOtpField) {
      onSendOtp(data);
    } else {
      onVerifyOtp(data);
    }
  };

  useEffect(() => {
    if (showOtpField) {
      setFocus("otp");
    }
  }, [showOtpField, setFocus]);

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0 && showOtpField) {
      setCanResend(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, showOtpField]);

  return (
    <div className="w-1/2 flex flex-col p-4 justify-center">
      <div className="flex items-center justify-center h-full">
        <div className="min-w-md">
          <div className="flex items-center justify-start w-full mb-4">
            <img src={LOGO} alt="Logo" className="h-12" />
          </div>

          {!showOtpField && (
            <>
              <div className="flex flex-col">
                <h1 className="text-lg font-bold ">Create Account</h1>
                <p className="text-gray-500 text-sm mb-4">
                  Please fill in your details to register
                </p>
              </div>
            </>
          )}

          <FormProvider {...formMethods}>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {!showOtpField ? (
                <div className="flex flex-col w-full gap-3">
                  <UiTextInput
                    name="mobile"
                    label="Mobile Number"
                    placeholder="Enter your mobile number"
                    maxLength={10}
                    registerOptions={{
                      required: "Mobile number is required",
                      pattern: {
                        value: inputUtility.indianMobileNumberRegex,
                        message: "Invalid Mobile Number",
                      },
                      onChange: (e) => {
                        inputUtility.onlyNumbers(e, 10);
                      },
                    }}
                    containerClass="gap-1"
                  />

                  <UiTextInput
                    name="name"
                    label="Full Name"
                    placeholder="Enter your full name"
                    registerOptions={{
                      required: "Full name is required",
                      maxLength: {
                        value: 100,
                        message: "Name must be under 100 characters",
                      },
                      onChange(event) {
                        inputUtility.onlyAlphabets(event);
                      },
                    }}
                    containerClass="gap-1"
                  />

                  <UiTextInput
                    name="email"
                    label="Email"
                    placeholder="Enter your email"
                    type="email"
                    registerOptions={{
                      required: "Email is required",
                      pattern: {
                        value: inputUtility.emailRegex,
                        message: "Invalid email address",
                      },
                      validate: (value: string) => {
                        const lower = value.toLowerCase();
                        return (
                          !inputUtility.blockedDomains.some((domain) =>
                            lower.endsWith("@" + domain)
                          ) || "Emails from this domain are not allowed"
                        );
                      },
                    }}
                    containerClass="gap-1"
                  />

                  <UiButton
                    type="submit"
                    buttonType="primary"
                    text="Sign Up"
                    isLoading={registerUserMutation.isPending}
                  />
                </div>
              ) : (
                <>
                  <div className="flex flex-col justify-start items-start">
                    <UiButton
                      type="button"
                      buttonType="tertiary"
                      icon={<ArrowLeft className="size-4" />}
                      text="Go Back"
                      onClick={handleBackToMobile}
                      className="text-sm text-primary flex items-center gap-1 flex-row-reverse px-2 py-1"
                    />
                    <h1 className="text-md font-bold mt-2">Verify OTP</h1>
                    <p className="text-gray-500 mb-4 text-sm ">
                      We&apos;ve sent a verification code to{" "}
                      {getValues("mobile")}
                    </p>
                  </div>
                  <div className="flex flex-col w-full gap-3">
                    <UiTextInput
                      key="otp-field"
                      name="otp"
                      label="Enter OTP"
                      placeholder="000000"
                      type="tel"
                      className="text-center w-full h-12 text-lg tracking-[0.3em] font-semibold border-2 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      registerOptions={{
                        required: "OTP is required",
                        maxLength: {
                          value: 6,
                          message: "OTP must be 6 digits",
                        },
                        onChange: (e) => inputUtility.onlyNumbers(e, 6),
                      }}
                      containerClass="gap-1"
                    />

                    <div className="text-center text-sm flex justify-end">
                      {timer > 0 ? (
                        <span className="text-gray-500">
                          Resend OTP in {timer}s
                        </span>
                      ) : (
                        canResend && (
                          <UiButton
                            type="button"
                            buttonType="tertiary"
                            text="Resend OTP"
                            onClick={handleResendOtp}
                            className="text-xs text-primary"
                          />
                        )
                      )}
                    </div>

                    <div className="flex w-full">
                      <UiButton
                        type="submit"
                        buttonType="primary"
                        text="Verify OTP"
                        className="flex-1"
                        isLoading={registerUserMutation.isPending}
                      />
                    </div>
                  </div>
                </>
              )}
            </form>
          </FormProvider>

          <div className="mt-4 text-center text-sm text-gray-500 flex justify-center items-center gap-2">
            <span>Already have an account?</span>
            <UiButton
              buttonType="tertiary"
              text="Sign In"
              onClick={onToggle}
              className="font-bold text-primary hover:text-primary/90"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
